/**
 * The institution's roster: doctors and staff, up to the admin's ceiling.
 *
 * A doctor created here holds an ordinary doctor account and uses the ordinary
 * doctor screens — their own calendar, clinics and prescriptions. The only
 * difference is where the money goes: their bookings credit the institution's
 * ledger rather than their own. That is the whole arrangement, and it is worth
 * saying on the screen because it is not otherwise visible anywhere.
 *
 * Removing a member suspends the account rather than deleting it. Their
 * bookings and prescriptions point at that user, and deleting it would orphan a
 * patient's history.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { useData } from '../../context/DataContext';
import { institutionApi } from '../../api/endpoints';
import { runsConsultations, quotaState } from '../../domain/institutions';
import { formatCurrency } from '../../domain/pricing';

const ROLE_LABEL = {
  manager: 'مدير المنشأة',
  doctor: 'طبيب',
  staff: 'موظف',
};

const emptyMember = () => ({
  memberRole: 'doctor',
  name: '',
  username: '',
  password: '',
  email: '',
  phone: '',
  title: '',
  categoryId: '',
  priceExam: 300,
  priceFollowup: 150,
});

export function InstitutionMembers() {
  const { success, error: toastError } = useNotifications();
  const { categories } = useData();

  const institution = useAsync(() => institutionApi.mine(), []);
  const state = useAsync(() => institutionApi.members(), [], { initialData: [] });

  const members = useMemo(() => state.data || [], [state.data]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);

  const active = useMemo(() => members.filter((m) => m.active), [members]);

  const quota = quotaState({
    used: active.length,
    limit: institution.data?.maxSubAccounts || 0,
  });

  const canAddDoctors = runsConsultations(institution.data?.type);

  const add = async (form) => {
    await institutionApi.addMember(form);
    success(`تم إنشاء حساب ${form.name}`);
    setAdding(false);
    state.reload();
    institution.reload();
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await institutionApi.removeMember(removing.id);
      success('تم إيقاف الحساب');
      state.reload();
      institution.reload();
    } catch (err) {
      toastError(err.message || 'تعذر الإيقاف');
    } finally {
      setRemoving(null);
    }
  };

  if (institution.error) return <ErrorState error={institution.error} onRetry={institution.reload} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="الأطباء والموظفون"
        subtitle={`${quota.used} من ${quota.limit} حساب`}
        icon="users"
        actions={
          <Button icon="user-plus" disabled={quota.full} onClick={() => setAdding(true)}>
            إضافة حساب
          </Button>
        }
      />

      <Panel className="border border-cyan-500/20 bg-cyan-500/[0.04]">
        <p className="flex items-start gap-2 text-xs leading-relaxed text-cyan-100/90">
          <Icon name="info" size={14} className="mt-0.5 shrink-0 text-cyan-400" />
          الأطباء هنا يستخدمون حساباتهم بشكل طبيعي — عياداتهم ومواعيدهم وروشتاتهم — والفرق أن إيرادات
          كشوفاتهم تُسجَّل في حساب المنشأة وليس في حساباتهم الشخصية.
        </p>
      </Panel>

      {quota.full && (
        <Panel className="border border-amber-500/25 bg-amber-500/[0.05]">
          <p className="flex items-center gap-2 text-sm text-amber-200">
            <Icon name="alert-circle" size={16} />
            {quota.exceeded
              ? `لديك ${quota.used} حساباً والحد المسموح ${quota.limit}. تواصل مع إدارة المنصة.`
              : `وصلت للحد الأقصى (${quota.limit} حساب). تواصل مع إدارة المنصة لرفعه.`}
          </p>
        </Panel>
      )}

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && members.length === 0 ? (
        <Spinner label="جاري تحميل الحسابات..." />
      ) : members.length === 0 ? (
        <Panel>
          <EmptyState
            icon="users"
            title="لا توجد حسابات بعد"
            message="أضف طبيباً ليبدأ في استقبال الحجوزات باسم المنشأة."
            action={
              <Button icon="user-plus" onClick={() => setAdding(true)}>
                إضافة حساب
              </Button>
            }
          />
        </Panel>
      ) : (
        /* grid-cols-1, not bare grid: an implicit track is auto-sized, so one
            long unbreakable name (a hospital's, a username, an email) widens
            it past the viewport. grid-cols-1 is minmax(0, 1fr), which caps
            the track at the container and lets the truncation inside work. */
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {members.map((member) => (
            <Panel key={member.id} className={`space-y-3 ${member.active ? '' : 'opacity-60'}`}>
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cyan-500/10 text-cyan-300">
                  <Icon name={member.memberRole === 'doctor' ? 'stethoscope' : 'user'} size={18} />
                </span>

                <div className="min-w-0 flex-1">
                  <h4 className="truncate font-bold text-white">
                    {member.user?.name || `حساب #${member.userId}`}
                  </h4>
                  <p className="truncate text-xs text-slate-400" dir="ltr">
                    @{member.user?.username || '—'}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Badge className="border-white/10 bg-white/5 text-slate-300">
                      {ROLE_LABEL[member.memberRole] || member.memberRole}
                    </Badge>
                    {!member.active && (
                      <Badge className="border-slate-600 bg-slate-800 text-slate-400">موقوف</Badge>
                    )}
                    {member.user?.DoctorProfile?.priceExam !== undefined && (
                      <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                        كشف {formatCurrency(member.user.DoctorProfile.priceExam)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {member.memberRole !== 'manager' && member.active && (
                <div className="flex justify-end border-t border-white/5 pt-3">
                  <Button
                    variant="subtle"
                    icon="user-x"
                    className="!text-red-300"
                    onClick={() => setRemoving(member)}
                  >
                    إيقاف الحساب
                  </Button>
                </div>
              )}
            </Panel>
          ))}
        </div>
      )}

      <MemberForm
        open={adding}
        canAddDoctors={canAddDoctors}
        categories={categories}
        onClose={() => setAdding(false)}
        onSubmit={add}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title="إيقاف الحساب"
        confirmLabel="إيقاف"
        message={`سيتم إيقاف ${removing?.user?.name || 'الحساب'} ولن يتمكن من الدخول. سجل حجوزاته وروشتاته يبقى كما هو.`}
      />
    </div>
  );
}

/* ========================================================================== */

function MemberForm({ open, canAddDoctors, categories, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyMember);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyMember(), memberRole: canAddDoctors ? 'doctor' : 'staff' });
    setError(null);
  }, [open, canAddDoctors]);

  if (!open) return null;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError(null);
  };

  const isDoctor = form.memberRole === 'doctor';

  const submit = async () => {
    if (!String(form.name).trim()) return setError('الاسم مطلوب');
    if (!String(form.username).trim()) return setError('اسم المستخدم مطلوب');
    if (String(form.password).length < 6) return setError('كلمة المرور 6 أحرف على الأقل');

    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      // 409 covers both a taken username and a full quota; the server's message
      // says which, and it is the actionable half.
      setError(err.message || 'تعذر الإنشاء');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="إضافة حساب"
      icon="user-plus"
      size="md"
      footer={
        <div className="flex items-center justify-between gap-3">
          {error && <span className="text-xs font-bold text-red-400">{error}</span>}
          <div className="ms-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={saving} onClick={submit}>
              إنشاء
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <Field label="نوع الحساب">
          <Select value={form.memberRole} onChange={update('memberRole')}>
            {canAddDoctors && <option value="doctor">طبيب</option>}
            <option value="staff">موظف</option>
          </Select>
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="الاسم" required>
            <Input value={form.name} onChange={update('name')} autoFocus />
          </Field>
          <Field label="اسم المستخدم" required>
            <Input value={form.username} onChange={update('username')} dir="ltr" />
          </Field>
        </div>

        <Field label="كلمة المرور" required hint="6 أحرف على الأقل">
          <Input type="password" value={form.password} onChange={update('password')} dir="ltr" />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="البريد">
            <Input type="email" value={form.email} onChange={update('email')} dir="ltr" />
          </Field>
          <Field label="الهاتف">
            <Input value={form.phone} onChange={update('phone')} dir="ltr" />
          </Field>
        </div>

        {isDoctor && (
          <section className="space-y-4 border-t border-white/10 pt-5">
            <h4 className="text-sm font-bold text-cyan-300">بيانات الطبيب</h4>
            <p className="text-[11px] leading-relaxed text-slate-500">
              يُنشأ ملف الطبيب تلقائياً — بدونه لا يظهر الطبيب للمرضى ولا يمكن حجز موعد معه.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="اللقب المهني">
                <Input value={form.title} onChange={update('title')} placeholder="استشاري باطنة" />
              </Field>
              <Field label="التخصص">
                <Select value={form.categoryId} onChange={update('categoryId')}>
                  <option value="">اختر التخصص</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="سعر الكشف">
                <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
              </Field>
              <Field label="سعر المتابعة">
                <Input
                  type="number"
                  min="0"
                  value={form.priceFollowup}
                  onChange={update('priceFollowup')}
                />
              </Field>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}

export default InstitutionMembers;
